import type { Editor, PointerInfo, Tool, ToolId } from "../editor";
import type { Vec } from "../geometry";

/** Hand: drag to pan, with a little momentum. */
export class HandTool implements Tool {
  readonly id: ToolId = "hand";
  private last: { p: Vec; t: number } | null = null;
  private vel: Vec = { x: 0, y: 0 };
  private raf: number | null = null;

  constructor(private editor: Editor) {}

  cursor(): string {
    return this.last ? "grabbing" : "grab";
  }

  onDown(p: PointerInfo): void {
    this.stopGlide();
    this.last = { p: p.screen, t: p.time };
    this.vel = { x: 0, y: 0 };
  }

  onMove(p: PointerInfo): void {
    if (!this.last) return;
    const dx = p.screen.x - this.last.p.x;
    const dy = p.screen.y - this.last.p.y;
    const dt = Math.max(1, p.time - this.last.t);
    this.vel = { x: (dx / dt) * 0.6 + this.vel.x * 0.4, y: (dy / dt) * 0.6 + this.vel.y * 0.4 };
    this.editor.camera.panBy(dx, dy);
    this.last = { p: p.screen, t: p.time };
  }

  onUp(p: PointerInfo): void {
    if (this.last && p.time - this.last.t < 60) this.glide();
    this.last = null;
  }

  onCancel(): void {
    this.last = null;
  }

  deactivate(): void {
    this.stopGlide();
  }

  private glide(): void {
    let v = { ...this.vel };
    let t = performance.now();
    const step = () => {
      const now = performance.now();
      const dt = now - t;
      t = now;
      v = { x: v.x * Math.pow(0.992, dt), y: v.y * Math.pow(0.992, dt) };
      if (Math.hypot(v.x, v.y) < 0.02) {
        this.raf = null;
        return;
      }
      this.editor.camera.panBy(v.x * dt, v.y * dt);
      this.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  }

  private stopGlide(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
  }
}

/** Laser pointer: a glowing trail that fades, for presenting (not saved; shown to viewers). */
export class LaserTool implements Tool {
  readonly id: ToolId = "laser";
  private pts: { p: Vec; t: number }[] = [];
  private raf: number | null = null;
  private down = false;
  onTrail?: (pts: Vec[]) => void;

  constructor(private editor: Editor) {}

  cursor(): string {
    return "crosshair";
  }

  onDown(p: PointerInfo): void {
    this.down = true;
    this.pts.push({ p: p.world, t: performance.now() });
    this.tick();
  }

  onMove(_p: PointerInfo, coalesced: PointerInfo[]): void {
    if (!this.down) return;
    for (const c of coalesced) this.pts.push({ p: c.world, t: performance.now() });
    this.onTrail?.(this.pts.slice(-40).map((x) => x.p));
  }

  onUp(): void {
    this.down = false;
  }

  onCancel(): void {
    this.down = false;
  }

  deactivate(): void {
    this.pts = [];
    this.editor.setOverlay({ laser: null });
  }

  private tick(): void {
    if (this.raf !== null) return;
    const step = () => {
      const now = performance.now();
      this.pts = this.pts.filter((x) => now - x.t < 900);
      this.editor.setOverlay({ laser: this.pts.length ? { pts: this.pts.slice() } : null });
      if (this.pts.length || this.down) this.raf = requestAnimationFrame(step);
      else this.raf = null;
    };
    this.raf = requestAnimationFrame(step);
  }
}
